import { useMemo } from "react";
import { Clock } from "lucide-react";

/**
 * PickupHoursCard — surfaces the same weekly pickup window used by
 * `/checkout` on the storefront About tab. Rendered only when the
 * seller has enabled pickup so buyers don't see a phantom card for
 * businesses that only sell online.
 */
const DAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"],
  ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];
const DOW_TODAY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function fmt12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const hr = ((h + 11) % 12) + 1;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function PickupHoursCard({ business }) {
  const f = business?.fulfillment;
  const todayKey = useMemo(() => DOW_TODAY[new Date().getDay()], []);
  if (!f?.pickup_enabled) return null;
  const windows = f.pickup_windows || {};
  const hasAny = DAYS.some(([k]) => windows[k]?.enabled && windows[k]?.open && windows[k]?.close);
  if (!hasAny) return null;

  return (
    <div className="bg-tbc-cream rounded-2xl p-5" data-testid="pickup-hours-card">
      <h3 className="font-heading text-lg text-tbc-navy mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4 text-tbc-gold" /> Pickup hours
      </h3>
      <div className="space-y-1.5">
        {DAYS.map(([k, label]) => {
          const w = windows[k];
          const isToday = k === todayKey;
          const open = w?.enabled && w?.open && w?.close;
          return (
            <div
              key={k}
              className={"flex justify-between text-sm font-body " + (isToday ? "text-tbc-navy font-semibold" : "text-gray-600")}
              data-testid={`pickup-hours-${k}`}
            >
              <span>{label}{isToday ? " · Today" : ""}</span>
              <span className={"tabular-nums " + (isToday ? "text-tbc-navy" : "text-tbc-navy/80")}>
                {open ? `${fmt12(w.open)} – ${fmt12(w.close)}` : "Closed"}
              </span>
            </div>
          );
        })}
      </div>
      {f.pickup_address && (
        <p className="mt-3 pt-3 border-t border-tbc-gold/20 text-xs text-gray-600 font-body">
          Pickup at <span className="text-tbc-navy">{f.pickup_address}</span>
        </p>
      )}
    </div>
  );
}
